/* The rule's quality dimension, as the design shows it under Kind: which score a failure lowers.
 *
 * For eight of the nine kinds it is a fact of the kind, so it is shown rather than asked. For custom
 * SQL it is a question, because nothing can tell what arbitrary SQL measures — and the server refuses
 * a custom SQL version that does not say. */

import type { ReactElement } from 'react';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';

import type { QualityDimension } from '@/shared/api/generated/rules';

import type { RuleKind } from '../api/rules';
import { CHOOSABLE_DIMENSIONS, dimensionOfKind } from '../model/dimensions';

/** The dimension a kind scores, as one line under the Kind field. */
export function DimensionLine({
  kind,
}: {
  readonly kind: RuleKind;
}): ReactElement | null {
  const { t } = useTranslation();
  const dimension = dimensionOfKind(kind);
  if (dimension === null) return null;
  return (
    <div className="lq-ru__dimLine" title={t('rules.dimension.how.fixed')}>
      {t('rules.dimension.label')}
      <span
        className="lq-ru__dimPill"
        title={t('rules.dimension.scores', {
          dimension: t(`scoring.dimension.${dimension}`).toLowerCase(),
        })}
      >
        {t(`scoring.dimension.${dimension}`)}
      </span>
      <span>{t(`rules.dimension.question.${dimension}`)}</span>
    </div>
  );
}

/** The dimension a custom SQL rule declares, chosen from the six a rule can measure. */
export function DimensionChoice({
  value,
  onChange,
  emptyLabel,
  error,
}: {
  readonly value: QualityDimension | '';
  readonly onChange: (next: QualityDimension | '') => void;
  /** What the unchosen state reads as: "choose later" on create, "choose one" on publish. */
  readonly emptyLabel: string;
  readonly error?: string | undefined;
}): ReactElement {
  const { t } = useTranslation();
  const errorId = useId();
  return (
    <div className="lq-ru__dimChoice">
      <label className="lq-ru__dimLine" title={t('rules.dimension.how.sql')}>
        {t('rules.dimension.label')}
        <select
          className="lq-input lq-ru__dimSelect"
          value={value}
          aria-describedby={error === undefined ? undefined : errorId}
          onChange={(event) => {
            const chosen = CHOOSABLE_DIMENSIONS.find(
              (dimension) => dimension === event.target.value,
            );
            onChange(chosen ?? '');
          }}
        >
          <option value="">{emptyLabel}</option>
          {CHOOSABLE_DIMENSIONS.map((dimension) => (
            <option key={dimension} value={dimension}>
              {t(`scoring.dimension.${dimension}`)}
            </option>
          ))}
        </select>
      </label>
      {value === '' ? null : (
        <span className="lq-ru__dimNote">{t(`rules.dimension.question.${value}`)}</span>
      )}
      {error === undefined ? null : (
        <p id={errorId} className="lq-ru__dimError" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
